import { useCallback, useMemo } from 'react';

import qs from 'qs';

import { useTracking } from '@change-corgi/core/react/tracking';

import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { useEncodedBanditDataForUrl, usePageCopy } from 'src/app/pages/psf/promoteOrShare/shared/hooks';
import { useQueryParamValue } from 'src/app/shared/hooks/location';

type Result = ModelHookResult<
	{
		promoteButtonLabel: string;
		promoteButtonUrl: string;
		shareButtonLabel: string;
		shareButtonUrl: string;
		paymentIconsLabel: string;
	},
	{
		handlePromoteClick: () => void;
		handleShareClick: () => void;
	}
>;

export function usePromoteOrShareActions(): Result {
	const {
		data: {
			petition: { slug },
		},
	} = usePsfPromoteOrSharePageData();
	const {
		data: { shareButtonText, promoteButtonWithGoalText, paymentIconsText },
	} = usePageCopy();

	const commonParams = useCommonParams();

	return {
		data: {
			promoteButtonLabel: promoteButtonWithGoalText,
			promoteButtonUrl: useMemo(
				() => `/p/${encodeURIComponent(slug)}/promote${qs.stringify(commonParams, { addQueryPrefix: true })}`,
				[slug, commonParams],
			),
			shareButtonLabel: shareButtonText,
			shareButtonUrl: useMemo(
				() =>
					`/p/${encodeURIComponent(slug)}/psf/share${qs.stringify(
						{
							...commonParams,
							share_intent: 1,
						},
						{ addQueryPrefix: true },
					)}`,
				[slug, commonParams],
			),
			paymentIconsLabel: paymentIconsText,
		},
		actions: useHandlers(),
	};
}

function useCommonParams() {
	const allowActions = useQueryParamValue('allow_actions');
	const sourceLocation = useQueryParamValue('source_location');
	const guest = useQueryParamValue('guest');
	const shortDisplayName = useQueryParamValue('short_display_name');
	const {
		data: { query: banditQueryParams },
	} = useEncodedBanditDataForUrl();

	return useMemo(
		() => ({
			source_location: sourceLocation || 'combo_psf',
			psf_variant: 'combo',
			...(['new', 'existing'].includes(guest || '') ? { from_guest_psf: '1' } : undefined),
			...(shortDisplayName ? { short_display_name: shortDisplayName } : undefined),
			...(allowActions ? { allow_actions: allowActions } : undefined),
			...banditQueryParams,
		}),
		[allowActions, sourceLocation, guest, shortDisplayName, banditQueryParams],
	);
}

function useHandlers() {
	const track = useTracking();

	return {
		handlePromoteClick: useCallback(() => {
			void track('combo_psf_promote');
		}, [track]),
		handleShareClick: useCallback(() => {
			void track('combo_psf_share');
		}, [track]),
	};
}
